import React, {
  useState,
  useEffect,
  useCallback,
  useMemo,
  useRef,
} from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import ProductCard from "./ProductCard";
import FloatingFilterButton from "./FloatingFilterButton";
import FilterSidebar from "./FilterSidebar";
import SearchBar from "./SearchBar";
import SortDropdown from "./SortDropdown";
import Pagination from "./Pagination";

import EmptyState from "../common/EmptyState";
import { SkeletonProductCard } from "../common/SkeletonLoader";
import { useProducts } from "../../hooks/useProducts";
import { apiRequest } from "../../config/api";
import { debounce } from "../../utils/helpers";
import QuickViewModal from "../shop/QuickViewModal";

// Skeleton loading component for products
const ProductSkeleton = React.memo(({ viewMode, gridColumns }) => {
  const getGridColsClass = (cols) => {
    switch (cols) {
      case 1:
        return "grid-cols-1";
      case 2:
        return "grid-cols-2 sm:grid-cols-2 md:grid-cols-2 lg:grid-cols-2 xl:grid-cols-2";
      case 3:
        return "grid-cols-1 sm:grid-cols-1 md:grid-cols-1 lg:grid-cols-3 xl:grid-cols-3";
      case 4:
        return "grid-cols-1 sm:grid-cols-1 md:grid-cols-1 lg:grid-cols-4 xl:grid-cols-4";
      case 5:
        return "grid-cols-1 sm:grid-cols-1 md:grid-cols-1 lg:grid-cols-4 xl:grid-cols-5";
      default:
        return "grid-cols-1 sm:grid-cols-1 md:grid-cols-1 lg:grid-cols-3 xl:grid-cols-3";
    }
  };

  return (
    <div className={`grid gap-3 ${getGridColsClass(gridColumns)}`}>
      {Array.from({ length: 12 }).map((_, index) => (
        <SkeletonProductCard key={index} />
      ))}
    </div>
  );
});

ProductSkeleton.displayName = "ProductSkeleton";

// Virtual scrolling component for large product lists
const VirtualizedProductGrid = React.memo(
  ({ products, viewMode, gridColumns, onProductClick }) => {
    const containerRef = useRef(null);
    const [visibleRange, setVisibleRange] = useState({ start: 0, end: 20 });
    const [containerHeight, setContainerHeight] = useState(0);

    const getGridColsClass = useCallback((cols) => {
      switch (cols) {
        case 1:
          return "grid-cols-1";
        case 2:
          return "grid-cols-2 sm:grid-cols-2 md:grid-cols-2 lg:grid-cols-2 xl:grid-cols-2";
        case 3:
          return "grid-cols-1 sm:grid-cols-1 md:grid-cols-1 lg:grid-cols-3 xl:grid-cols-3";
        case 4:
          return "grid-cols-1 sm:grid-cols-1 md:grid-cols-1 lg:grid-cols-4 xl:grid-cols-4";
        case 5:
          return "grid-cols-1 sm:grid-cols-1 md:grid-cols-1 lg:grid-cols-4 xl:grid-cols-5";
        default:
          return "grid-cols-1 sm:grid-cols-1 md:grid-cols-1 lg:grid-cols-3 xl:grid-cols-3";
      }
    }, []);

    // Use intersection observer for lazy loading
    useEffect(() => {
      const observer = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting) {
              const productId = entry.target.dataset.productId;
              // Trigger lazy loading for images
              const img = entry.target.querySelector("img[data-src]");
              if (img) {
                img.src = img.dataset.src;
                img.removeAttribute("data-src");
              }
            }
          });
        },
        { rootMargin: "50px" }
      );

      const cards = containerRef.current?.querySelectorAll("[data-product-id]");
      cards?.forEach((card) => observer.observe(card));

      return () => observer.disconnect();
    }, [products]);

    // For smaller lists, render normally
    if (products.length <= 50) {
      return (
        <div className={`grid gap-3 ${getGridColsClass(gridColumns)}`}>
          {products.map((product) => (
            <div key={product.id} data-product-id={product.id}>
              <ProductCard
                product={product}
                viewMode={viewMode}
                gridColumns={gridColumns}
                onQuickView={onProductClick}
              />
            </div>
          ))}
        </div>
      );
    }

    // For larger lists, use virtual scrolling
    const itemHeight = Math.max(350, Math.min(450, window.innerHeight * 0.6)); // Responsive height
    const visibleCount = Math.ceil(containerHeight / itemHeight) + 6; // Increased buffer for better UX

    const visibleProducts = products.slice(
      visibleRange.start,
      visibleRange.end
    );
    const topOffset = visibleRange.start * itemHeight;

    return (
      <div
        ref={containerRef}
        className="relative"
        style={{ height: `${products.length * itemHeight}px` }}
      >
        <div
          className={`absolute top-0 left-0 right-0 grid gap-3 ${getGridColsClass(
            gridColumns
          )}`}
          style={{ transform: `translateY(${topOffset}px)` }}
        >
          {visibleProducts.map((product) => (
            <div key={product.id} data-product-id={product.id}>
              <ProductCard
                product={product}
                viewMode={viewMode}
                gridColumns={gridColumns}
                onQuickView={onProductClick}
              />
            </div>
          ))}
        </div>
      </div>
    );
  }
);

VirtualizedProductGrid.displayName = "VirtualizedProductGrid";

const ShopPage = React.memo(({ section } = {}) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();

  // Memoized helper function to get grid column classes
  const getGridColsClass = useCallback((cols) => {
    switch (cols) {
      case 1:
        return "grid-cols-1";
      case 2:
        return "grid-cols-2 sm:grid-cols-2 md:grid-cols-2 lg:grid-cols-2 xl:grid-cols-2";
      case 3:
        return "grid-cols-1 sm:grid-cols-1 md:grid-cols-1 lg:grid-cols-3 xl:grid-cols-3";
      case 4:
        return "grid-cols-1 sm:grid-cols-1 md:grid-cols-1 lg:grid-cols-4 xl:grid-cols-4";
      case 5:
        return "grid-cols-1 sm:grid-cols-1 md:grid-cols-1 lg:grid-cols-4 xl:grid-cols-5";
      default:
        return "grid-cols-1 sm:grid-cols-1 md:grid-cols-1 lg:grid-cols-3 xl:grid-cols-3";
    }
  }, []);

  // State management with optimized initial values
  const [filters, setFilters] = useState(() => ({
    search: searchParams.get("search") || "",
    categories: searchParams.getAll("categories") || [],
    tags: searchParams.getAll("tags") || [],
    minPrice: searchParams.get("minPrice") || "",
    maxPrice: searchParams.get("maxPrice") || "",
    brand: searchParams.get("brand") || "",
    minRating: searchParams.get("minRating") || "",
    inStock: searchParams.get("outOfStock") === "true"
      ? false
      : searchParams.get("inStock") !== "false",
    outOfStock: searchParams.get("outOfStock") === "true",
    featured: searchParams.get("featured") === "true",
  }));

  const [currentPage, setCurrentPage] = useState(
    parseInt(searchParams.get("page")) || 1
  );

  const [sortBy, setSortBy] = useState(searchParams.get("sort") || "relevance");
  const [viewMode] = useState("grid");
  const [quickViewOpen, setQuickViewOpen] = useState(false);
  const [quickViewProduct, setQuickViewProduct] = useState(null);

  // Memoized responsive default: 2 for mobile, 2 for tablet, 3 for desktop
  const getInitialGridColumns = useCallback(() => {
    if (typeof window !== "undefined") {
      if (window.innerWidth >= 1024) {
        return 3; // Desktop default
      } else if (window.innerWidth >= 768) {
        return 2; // Tablet default
      } else {
        return 2; // Mobile default - two columns for better mobile UX
      }
    }
    return 2; // Fallback to two columns
  }, []);

  const [gridColumns, setGridColumns] = useState(getInitialGridColumns);
  const [availableFilters, setAvailableFilters] = useState({});
  const [showFilters, setShowFilters] = useState(false);

  // Handle responsive grid columns on window resize
  useEffect(() => {
    const handleResize = () => {
      const newColumns = getInitialGridColumns();
      if (newColumns !== gridColumns) {
        setGridColumns(newColumns);
      }
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, [gridColumns, getInitialGridColumns]);

  // Memoized filters object to prevent infinite re-renders
  const memoizedFilters = useMemo(
    () => ({
      page: currentPage,
      limit: 24, // Increased from 20 for better performance
      sort: sortBy,
      ...filters,
    }),
    [currentPage, sortBy, filters]
  );

  // Fetch products with current filters - section switches API (accessories / yarns / all)
  const { products, loading, error, pagination, refreshProducts } =
    useProducts(memoizedFilters, { section });

  // Debug logging
  useEffect(() => {
    if (pagination?.totalPages > 1) {
      console.log("🔍 ShopPage:", {
        productsCount: products.length,
        totalProducts: pagination?.totalProducts,
        currentPage: currentPage,
        totalPages: pagination?.totalPages,
      });
    }
  }, [products, pagination, currentPage]);

  // Memoized product count for performance
  const productCount = useMemo(() => products.length, [products.length]);
  const totalProducts = useMemo(
    () => pagination?.totalProducts || 0,
    [pagination?.totalProducts]
  );

  // Fetch available filters with caching
  const fetchAvailableFilters = useCallback(async () => {
    try {
      const data = await apiRequest("/products/available-filters");
      setAvailableFilters(data.data);
    } catch (error) {
      console.error("Failed to fetch filter options:", error);
    }
  }, []);

  useEffect(() => {
    fetchAvailableFilters();
  }, [fetchAvailableFilters]);

  // Section-specific filter options: hide category on accessories; exclude accessories on yarns
  const sectionFilterOptions = useMemo(() => {
    if (!availableFilters.categories) return availableFilters;
    if (section === "accessories") {
      return { ...availableFilters, categories: [] };
    }
    if (section === "yarns") {
      const categories =
        availableFilters.categories.filter(
          (c) => String(c?.slug || "").toLowerCase() !== "accessories"
        ) || [];
      return { ...availableFilters, categories };
    }
    return availableFilters;
  }, [availableFilters, section]);

  // Create stable debounced search function with longer delay for better performance
  const debouncedSearchFunction = useMemo(
    () =>
      debounce((searchTerm) => {
        setFilters((prev) => ({ ...prev, search: searchTerm }));
        setCurrentPage(1);
      }, 500), // Optimized for responsive search experience
    []
  );

  // Debounced search function
  const debouncedSearch = useCallback(
    (searchTerm) => {
      debouncedSearchFunction(searchTerm);
    },
    [debouncedSearchFunction]
  );

  // Optimized URL params update with memoization
  const updateURLParams = useCallback(() => {
    const params = new URLSearchParams();

    Object.entries(filters).forEach(([key, value]) => {
      if (value !== null && value !== undefined && value !== "") {
        if (Array.isArray(value)) {
          if (value.length > 0) {
            value.forEach((v) => params.append(key, v));
          }
        } else if (typeof value === "boolean") {
          params.set(key, value.toString());
        } else if (value !== false) {
          params.set(key, value.toString());
        }
      }
    });

    if (currentPage > 1) {
      params.set("page", currentPage.toString());
    }

    if (sortBy !== "relevance") {
      params.set("sort", sortBy);
    }

    setSearchParams(params);
  }, [filters, currentPage, sortBy, setSearchParams]);

  // Debounce URL updates for better performance
  useEffect(() => {
    const timeoutId = setTimeout(updateURLParams, 100);
    return () => clearTimeout(timeoutId);
  }, [updateURLParams]);

  // Handle filter changes with optimized state updates
  const handleFilterChange = useCallback((filterType, value) => {
    setFilters((prev) => {
      const newFilters = { ...prev };

      if (filterType === "categories" || filterType === "tags") {
        const currentValues = prev[filterType] || [];
        if (currentValues.includes(value)) {
          newFilters[filterType] = currentValues.filter((v) => v !== value);
        } else {
          newFilters[filterType] = [...currentValues, value];
        }
      } else if (filterType === "inStock") {
        newFilters.inStock = value;
        if (value) newFilters.outOfStock = false;
      } else if (filterType === "outOfStock") {
        newFilters.outOfStock = value;
        if (value) newFilters.inStock = false;
      } else {
        newFilters[filterType] = value;
      }

      return newFilters;
    });
    setCurrentPage(1);
  }, []);

  // Handle search
  const handleSearch = useCallback(
    (searchTerm) => {
      debouncedSearch(searchTerm);
    },
    [debouncedSearch]
  );

  // Handle sort change
  const handleSortChange = useCallback((newSort) => {
    setSortBy(newSort);
    setCurrentPage(1);
  }, []);

  // Handle page change with smooth scroll
  const handlePageChange = useCallback((page) => {
    setCurrentPage(page);
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, []);

  // Clear all filters
  const clearAllFilters = useCallback(() => {
    setFilters({
      search: "",
      categories: [],
      tags: [],
      minPrice: "",
      maxPrice: "",
      brand: "",
      minRating: "",
      inStock: true,
      outOfStock: false,
      featured: false,
    });
    setCurrentPage(1);
    setSortBy("relevance");
  }, []);

  // Memoized check if any filters are active
  const hasActiveFilters = useMemo(() => {
    return Object.entries(filters).some(([key, value]) => {
      if (key === "search") return value !== "";
      if (Array.isArray(value)) return value.length > 0;
      if (typeof value === "boolean") return value;
      return value !== "";
    });
  }, [filters]);

  // Handle product quick view
  const handleProductQuickView = useCallback((product) => {
    setQuickViewProduct(product);
    setQuickViewOpen(true);
  }, []);

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="text-red-500 text-xl mb-4">
            ⚠️ Error Loading Products
          </div>
          <p className="text-gray-600 mb-4">{error}</p>
          <button
            onClick={refreshProducts}
            className="bg-blue-600 text-white px-6 py-2 rounded-lg hover:bg-blue-700 transition-colors"
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header Section */}
      <div className="bg-white border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div>
              <h1 className="text-2xl font-bold text-gray-900">
                {section === "accessories"
                  ? "Accessories"
                  : section === "yarns"
                    ? "Yarns"
                    : "Shop"}
              </h1>
              <p className="text-gray-600 mt-1">
                {totalProducts} products available
              </p>
            </div>

            <div className="flex flex-col sm:flex-row gap-3">
              <SearchBar
                value={filters.search}
                onChange={handleSearch}
                placeholder="Search products..."
                className="hidden sm:block w-full sm:w-64"
                showSuggestions={true}
                onSuggestionSelect={(suggestion) => {
                  navigate(`/products/${suggestion.slug}`);
                }}
                onSearchSubmit={(query) => {
                  handleSearch(query);
                }}
              />
              <SortDropdown
                value={sortBy}
                onChange={handleSortChange}
                className="hidden sm:block w-full sm:w-auto"
              />
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        <div className="flex flex-col lg:flex-row gap-6">
          {/* Desktop Filter Sidebar - Hidden on mobile */}
          <div className="hidden lg:block lg:w-64 flex-shrink-0">
            <FilterSidebar
              filters={filters}
              availableFilters={sectionFilterOptions}
              onFilterChange={handleFilterChange}
              onClearAll={clearAllFilters}
              hasActiveFilters={hasActiveFilters}
              sortBy={sortBy}
              onSortChange={handleSortChange}
              section={section}
            />
          </div>

          {/* Products Section */}
          <div className="flex-1">
            {/* Desktop Filter Toggle - Only visible on desktop */}
            <div className="hidden lg:block mb-4">
              <button
                onClick={() => setShowFilters(!showFilters)}
                className="w-full bg-white border border-gray-300 px-4 py-2 text-left text-sm font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <span className="flex items-center justify-between">
                  <span>Filters</span>
                  <span className="text-gray-400">
                    {hasActiveFilters && (
                      <span className="inline-flex items-center px-2 py-0.5 text-xs font-medium bg-blue-100 text-blue-800 ml-2">
                        Active
                      </span>
                    )}
                  </span>
                </span>
              </button>
              {showFilters && (
                <div className="mt-2 bg-white border border-gray-300 p-4">
                  <FilterSidebar
                    filters={filters}
                    availableFilters={sectionFilterOptions}
                    onFilterChange={handleFilterChange}
                    onClearAll={clearAllFilters}
                    hasActiveFilters={hasActiveFilters}
                    sortBy={sortBy}
                    onSortChange={handleSortChange}
                    section={section}
                  />
                </div>
              )}
            </div>

            {/* View Controls (grid only) */}
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center space-x-4">
                {viewMode === "grid" && (
                  <div className="flex items-center space-x-2">
                    {/* Mobile: 1, 2 columns; Desktop: 1, 2, 3, 4, 5 columns */}
                    {[1, 2, 3, 4, 5].map((cols) => (
                      <button
                        key={cols}
                        onClick={() => setGridColumns(cols)}
                        className={`p-2 border ${
                          gridColumns === cols
                            ? "bg-blue-600 text-white border-blue-600"
                            : "bg-gray-200 text-gray-700 border-gray-300 hover:bg-gray-300"
                        } ${
                          cols === 1 ? "inline-flex" : "" // Always show 1 column
                        } ${
                          cols === 2 ? "inline-flex" : "" // Always show 2 columns
                        } ${
                          cols === 3 ? "hidden lg:inline-flex" : "" // Show 3+ on large screens only
                        } ${
                          cols === 4 ? "hidden lg:inline-flex" : "" // Show 4+ on large screens only
                        } ${
                          cols === 5 ? "hidden xl:inline-flex" : "" // Show 5 on extra large screens only
                        }`}
                        aria-label={`Show ${cols} columns`}
                      >
                        <svg
                          width="20"
                          height="20"
                          viewBox="0 0 20 20"
                          fill="none"
                          xmlns="http://www.w3.org/2000/svg"
                        >
                          {cols === 1 && (
                            <rect
                              x="4"
                              y="4"
                              width="12"
                              height="12"
                              rx="0"
                              fill="currentColor"
                            />
                          )}
                          {cols === 2 && (
                            <>
                              <rect
                                x="3"
                                y="4"
                                width="6"
                                height="12"
                                rx="0"
                                fill="currentColor"
                              />
                              <rect
                                x="11"
                                y="4"
                                width="6"
                                height="12"
                                rx="0"
                                fill="currentColor"
                              />
                            </>
                          )}
                          {cols === 3 && (
                            <>
                              <rect
                                x="2"
                                y="4"
                                width="4"
                                height="12"
                                rx="0"
                                fill="currentColor"
                              />
                              <rect
                                x="8"
                                y="4"
                                width="4"
                                height="12"
                                rx="0"
                                fill="currentColor"
                              />
                              <rect
                                x="14"
                                y="4"
                                width="4"
                                height="12"
                                rx="0"
                                fill="currentColor"
                              />
                            </>
                          )}
                          {cols === 4 && (
                            <>
                              <rect
                                x="1"
                                y="4"
                                width="3.5"
                                height="12"
                                rx="0"
                                fill="currentColor"
                              />
                              <rect
                                x="6"
                                y="4"
                                width="3.5"
                                height="12"
                                rx="0"
                                fill="currentColor"
                              />
                              <rect
                                x="11"
                                y="4"
                                width="3.5"
                                height="12"
                                rx="0"
                                fill="currentColor"
                              />
                              <rect
                                x="16"
                                y="4"
                                width="3.5"
                                height="12"
                                rx="0"
                                fill="currentColor"
                              />
                            </>
                          )}
                          {cols === 5 && (
                            <>
                              <rect
                                x="1"
                                y="4"
                                width="2.8"
                                height="12"
                                rx="0"
                                fill="currentColor"
                              />
                              <rect
                                x="5"
                                y="4"
                                width="2.8"
                                height="12"
                                rx="0"
                                fill="currentColor"
                              />
                              <rect
                                x="9"
                                y="4"
                                width="2.8"
                                height="12"
                                rx="0"
                                fill="currentColor"
                              />
                              <rect
                                x="13"
                                y="4"
                                width="2.8"
                                height="12"
                                rx="0"
                                fill="currentColor"
                              />
                              <rect
                                x="17"
                                y="4"
                                width="2.8"
                                height="12"
                                rx="0"
                                fill="currentColor"
                              />
                            </>
                          )}
                        </svg>
                      </button>
                    ))}
                  </div>
                )}
              </div>
            </div>

            {/* Products Grid/List with Optimized Loading */}
            {loading ? (
              <ProductSkeleton viewMode={viewMode} gridColumns={gridColumns} />
            ) : productCount === 0 ? (
              <EmptyState
                title="No products found"
                description="Try adjusting your filters or search terms."
                action={
                  <button
                    onClick={clearAllFilters}
                    className="bg-blue-600 text-white px-4 py-2 hover:bg-blue-700"
                  >
                    Clear all filters
                  </button>
                }
              />
            ) : (
              <VirtualizedProductGrid
                products={products}
                viewMode={viewMode}
                gridColumns={gridColumns}
                onProductClick={handleProductQuickView}
              />
            )}

            {/* Pagination - Show when there are products and multiple pages */}
            {!loading && products.length > 0 && pagination?.totalPages > 1 && (
              <div className="mt-8 bg-white rounded-lg shadow-sm p-6">
                <div className="text-sm text-gray-500 mb-4 text-center">
                  {pagination?.totalPages > 1 ? (
                    <>
                      Showing {(currentPage - 1) * 24 + 1} to{" "}
                      {Math.min(currentPage * 24, totalProducts)} of{" "}
                      {totalProducts} products
                    </>
                  ) : (
                    `Showing ${totalProducts} products`
                  )}
                </div>
                {pagination?.totalPages > 1 && (
                  <Pagination
                    currentPage={currentPage}
                    totalPages={pagination.totalPages}
                    onPageChange={handlePageChange}
                    hasNext={currentPage < pagination.totalPages}
                    hasPrev={currentPage > 1}
                    className="border-t pt-4"
                  />
                )}
                {/* Debug info - Only show in development */}
                {process.env.NODE_ENV === "development" && (
                  <div className="text-xs text-gray-400 mt-2 text-center">
                    Page {currentPage} of {pagination?.totalPages || 1} | Total:{" "}
                    {totalProducts} products
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Mobile Floating Filter Button - Hidden on desktop */}
      <div className="lg:hidden">
        <FloatingFilterButton
          filters={filters}
          availableFilters={sectionFilterOptions}
          onFilterChange={handleFilterChange}
          onClearAll={clearAllFilters}
          hasActiveFilters={hasActiveFilters}
          sortBy={sortBy}
          onSortChange={handleSortChange}
          section={section}
        />
      </div>
      {quickViewOpen && quickViewProduct && (
        <QuickViewModal
          product={quickViewProduct}
          open={quickViewOpen}
          onClose={() => setQuickViewOpen(false)}
        />
      )}
    </div>
  );
});

ShopPage.displayName = "ShopPage";

export default ShopPage;
